import { Injectable, inject } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries

import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ApolloInstances } from '../graphql.module';
import {
  PrimaryCategories,
  CategoriesForOrganization,
  CategorizationSchemeStatistic,
  PotentialSavingsStatistic,
  PurchasedProducts,
  ProductSearchGraphqlResponse,
} from '../../../types/types';
import { AssignableField } from '../../../modules/upload/upload-requests-api';
import GET_POTENTIAL_SAVINGS_STATISTICS from './queries/get-potential-savings-statistics';
import GET_SEARCH_PRODUCTS from './queries/get-search-products';
import GET_ALL_CATEGORIES_FOR_ORGANIZATION from '../allCategoriesForOrganization';
import GET_CATEGORIZATION_SCHEME_STATISTICS from './queries/get-categorization-scheme-statistics';
import GET_CATEGORIZATION_SCHEMES from './queries/get-categorization-schemes';
import GET_CATEGORIES from './queries/get-categories';
import DOWNLOAD_CROSS_REFERENCES from './queries/download-cross-references';
import DOWNLOAD_PRODUCT_ATTRIBUTES from './queries/download-product-attributes';
import { CATALOG_PURCHASED_PRODUCTS } from './queries/catalog-purchased-products.graphql';
import GET_CROSS_REFERENCE_ASSIGNABLE_FIELDS from '../../../services/api/queries/get-cross-reference-assignable-fields';
import { PublicCategorizationSchemes } from '@aptitude/shared-vanilla';
interface CategorizationScheme {
  id: number;
  name: string;
  type: string;
}

export interface DownloadOptionalParameters {
  categorizationSchemeId?: number;
  categoryId?: number;
  productId?: number;
  searchValue?: string;
  filterDate?: string;
  eppAttributes?: string[];
  productAttributeNames?: string[];
}
@Injectable({
  providedIn: 'root',
})
export class CatalogGraphqlService {
  private catalogClient = inject(Apollo).use(ApolloInstances.CATALOG);
  constructor(private apollo: Apollo) {}

  getCategories(organizationId: number, useNewPscLabel = false) {
    return this.apollo
      .use('catalog')
      .watchQuery<{ primaryCategories: Array<PrimaryCategories> }>({
        query: GET_CATEGORIES,
        variables: { organizationId: organizationId },
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          if (useNewPscLabel) {
            return request.data.primaryCategories.map((category) => {
              if (
                category.categorizationScheme?.id ===
                PublicCategorizationSchemes.CC
              ) {
                return {
                  ...category,
                  categorizationScheme: {
                    ...category.categorizationScheme,
                    name: 'CC',
                  },
                };
              }
              return category;
            });
          }
          return request.data.primaryCategories;
        }),
      );
  }

  getCategorizationSchemes(organizationId: number, useNewPscLabel = false) {
    return this.apollo
      .use('catalog')
      .watchQuery<{ categorizationSchemes: Array<CategorizationScheme> }>({
        query: GET_CATEGORIZATION_SCHEMES,
        variables: { organizationId: organizationId },
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          let rawSchemes: Array<CategorizationScheme> =
            request.data.categorizationSchemes;

          const convertedSchemes = useNewPscLabel
            ? rawSchemes.map((scheme) =>
                scheme.id === PublicCategorizationSchemes.CC //PSC
                  ? { ...scheme, name: 'CC' }
                  : scheme,
              )
            : rawSchemes;

          return convertedSchemes;
        }),
      );
  }

  getAllCategoriesForOrganization(
    organizationId: number,
    categorizationSchemeName = 'aptitude',
  ): Observable<Array<CategoriesForOrganization>> {
    const contextName = 'CUSTOM_CATEGORIES_FOR_CONTRACT_CREATION';
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .watchQuery<{ primaryCategories: Array<CategoriesForOrganization> }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId, contextName, categorizationSchemeName },
        query: GET_ALL_CATEGORIES_FOR_ORGANIZATION,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.primaryCategories),
      );
  }

  private validateParamIsCorrect(param: string, name: string) {
    if (!param?.length || param?.length === 0) {
      throw new Error(
        `The dev is trying to do a grapqhl request with an invalid ${name}: ${param}`,
      );
    }
    return true;
  }

  /**
   * Returns the potential savings statistics by organization Id
   *
   * @param {string} organizationId The Id of the org.
   * @return {Observable<PotentialSavingsStatistic[] | undefined>} observable of the potential savings statistics list.
   */
  getPotentialSavingsStatistics(
    organizationId: string,
    categorizationSchemeName: string,
  ): Observable<Array<PotentialSavingsStatistic>> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    this.validateParamIsCorrect(
      categorizationSchemeName,
      'categorizationSchemeName',
    );

    return this.apollo
      .use(ApolloInstances.CATALOG)
      .watchQuery<{
        catalogStatistics: {
          potentialSavingsStatistics: Array<PotentialSavingsStatistic>;
        };
      }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId, categorizationSchemeName },
        query: GET_POTENTIAL_SAVINGS_STATISTICS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data.catalogStatistics.potentialSavingsStatistics;
        }),
      );
  }

  getSearchProducts(searchValue: string, isPscRebranding = false) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{
        search: ProductSearchGraphqlResponse;
      }>({
        query: GET_SEARCH_PRODUCTS,
        variables: { searchValue: searchValue },
      })
      .pipe(
        map(({ data }) => {
          return data.search;
        }),
        map((searchProductsResponse) => {
          const buckets = searchProductsResponse.buckets.map((bucket) => {
            if (isPscRebranding && bucket.name === 'PSC') {
              bucket.name = 'CC';
            }

            return bucket;
          });

          return { type: '', buckets };
        }),
      );
  }
  /**
   * Returns the categorization scheme statistics by organization Id
   *
   * @param {string} organizationId The Id of the org.
   * @param {string} categorizationSchemeName The categorization scheme name.
   * @param {string} categorizationSchemeId The categorization scheme id.
   * @return {Observable<PotentialSavingsStatistic[] | undefined>} observable of the potential savings statistics list.
   */
  getCategorizationSchemeStatistics(
    organizationId: string,
    categorizationSchemeName: string,
    categorizationSchemeId: string,
  ): Observable<CategorizationSchemeStatistic> {
    this.validateParamIsCorrect(organizationId, 'organizationId');
    this.validateParamIsCorrect(
      categorizationSchemeName,
      'categorizationSchemeName',
    );
    this.validateParamIsCorrect(
      categorizationSchemeId,
      'categorizationSchemeId',
    );

    return this.apollo
      .use(ApolloInstances.CATALOG)
      .watchQuery<{
        catalogStatistics: {
          categorizationSchemeStatistics: CategorizationSchemeStatistic;
        };
      }>({
        fetchPolicy: 'no-cache',
        variables: {
          organizationId,
          categorizationSchemeName,
          categorizationSchemeId,
        },
        query: GET_CATEGORIZATION_SCHEME_STATISTICS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => {
          return request.data.catalogStatistics.categorizationSchemeStatistics;
        }),
      );
  }

  downloadCrossReferences(downloadParams: DownloadOptionalParameters) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{
        downloadCrossReferences: string;
      }>({
        fetchPolicy: 'no-cache',
        query: DOWNLOAD_CROSS_REFERENCES(downloadParams),
      })
      .pipe(
        map(({ data }) => {
          return data.downloadCrossReferences;
        }),
      );
  }

  downloadProductAttributes(downloadParams: DownloadOptionalParameters) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{
        downloadProductAttributes: string;
      }>({
        fetchPolicy: 'no-cache',
        query: DOWNLOAD_PRODUCT_ATTRIBUTES(downloadParams),
      })
      .pipe(
        map(({ data }) => {
          return data.downloadProductAttributes;
        }),
      );
  }

  getPurchasedProducts(
    buyerId: number,
    contextIds: number[],
  ): Observable<Array<PurchasedProducts>> {
    return this.catalogClient
      .query<{
        purchasedProducts: Array<PurchasedProducts>;
      }>({
        fetchPolicy: 'no-cache',
        query: CATALOG_PURCHASED_PRODUCTS,
        variables: {
          buyerId,
          contextIds,
        },
      })
      .pipe(
        map((request) => request.data.purchasedProducts),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      );
  }

  getAssignableFields() {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ crossReferenceAssignableFields: Array<AssignableField> }>({
        fetchPolicy: 'no-cache',
        query: GET_CROSS_REFERENCE_ASSIGNABLE_FIELDS,
      })
      .pipe(map(({ data }) => data.crossReferenceAssignableFields));
  }
}
